<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import { MapPreset } from '~/types/types'
import AppMap from '@/components/map/AppMap.vue'
import { LayerPresets } from '~/types/map'
import AppLoadingWrapper from '~/components/app/AppLoadingWrapper.vue'
import { FilterPresets } from '~/types/filters'
import MapSplitView from '~/components/layout/MapSplitView.vue'

definePageMeta({
  layout: 'full-width',
})
const { slug } = useRoute().params
const { updateFiltersFromQueryParams } = useGlobalFilters()

const { find } = useStrapi()
const loadMap = ref(false)
onMounted(() => {
  updateFiltersFromQueryParams()
  setTimeout(() => {
    loadMap.value = true
  }, 1000)
})
const mapPreset: Ref<MapPreset | undefined> = ref()
const layerPresetsConfig: { [key: string]: LayerPresets } = {
  'infrastructure-overall': {
    municipalities: { visible: false },
    stations: {
      visible: true,
      dataFilters: ['station.fuelTypes', 'station.stationType'],
      dataLegendLabel: 'Visar tankstationer för fossilfria drivmedel',
    },
    counties: { visible: true },
  },
  'vehicle-fleet': {
    municipalities: { visible: false },
    counties: { visible: false },
    'area-heat-map': {
      visible: true,
      name: 'Fördelningskarta fordonsflotta',
      dataLegendLabel: 'Visar andel fordon av vald drivmedel i relation till total fordonsflotta',
    },
  },
  'infrastructure-opportunities': {
    stations: {
      visible: false,
      name: 'Drivmedelsstationer',
      dataFilters: ['station.fuelTypes', 'station.stationType'],
    },
    'infrastructure-opportunities': {
      visible: true,
      name: 'Samarbeta',
      dataLegendLabel: 'Visar platser med uttryckt behov eller utbud av framtida infrastruktur',
    },
  },
  'infrastructure-charging-stations': {
    stations: {
      visible: true,
      name: 'Laddstationer',
      dataFilters: ['station.stationType'],
    },
    'infrastructure-opportunities': { visible: true },
    municipalities: { visible: false },
    counties: { visible: false },
  },
  'infrastructure-gas-fuel-stations': {
    stations: {
      visible: true,
      name: 'Tankstationer flytande fossilfri gas',
    },
    municipalities: { visible: false },
    counties: { visible: false },
  },
  'infrastructure-liquid-fuel-stations': {
    stations: {
      visible: true,
      name: 'Tankstationer flytande fossilfritt bränsle',
    },
    municipalities: { visible: false },
    counties: { visible: false },
  },
  'electricity-grid-owners': {
    municipalities: { visible: false },
    counties: { visible: false },
    stations: {
      visible: false,
    },
    'electricity-grid': {
      visible: true,
      name: 'Elnätsägare',
      dataLegendLabel: 'Visar elnätsägare',
    },
  },
  afir: {
    municipalities: { visible: false },
    counties: { visible: false },
    'area-heat-map': {
      visible: true,
      name: 'AFIR',
      dataLegendLabel: 'Indikerar hur mycket laddinfrastruktur som finns i olika delar av landet jämfört med de nationella AFIR målen. AFIR ställer krav på Sverige som helhet.',
    },
    stations: {
      visible: false,
    }
  },
}
const layerPresets: Ref<LayerPresets | undefined> = ref()

const filterPresets: FilterPresets = {
  'infrastructure-charging-stations': {
    stations: [
      { filterIdentifier: 'electricityOrigin', values: ['uppladdning'] },
      { filterIdentifier: 'fuelTypeGroup', values: ['electricity'] },
    ],
    matchmaking: [{ filterIdentifier: 'fuelTypeGroup', values: ['electricity'] }],
  },
  'infrastructure-liquid-fuel-stations': {
    stations: [{ filterIdentifier: 'fuelTypeGroup', values: ['liquid'] }],
    matchmaking: [{ filterIdentifier: 'fuelTypeGroup', values: ['liquid'] }],
  },
  'infrastructure-gas-fuel-stations': {
    stations: [{ filterIdentifier: 'fuelTypeGroup', values: ['gas'] }],
    matchmaking: [{ filterIdentifier: 'fuelTypeGroup', values: ['gas'] }],
  },
}
const { applyFilterPreset } = useGlobalFilters()

await find<MapPreset>('map-presets', {
  filters: {
    slug: {
      $eq: slug,
    },
  },
})
  .then((response) => {
    mapPreset.value = response.data[0].attributes
    layerPresets.value = layerPresetsConfig[mapPreset.value.identifier]
    applyFilterPreset(filterPresets[mapPreset.value.identifier])
    // updateFiltersFromQueryParams()
  })
  .catch((error) => {
    console.error(error)
  })

useSeoMeta({
  title: () =>
    `Drivmedla - ${mapPreset.value ? `Karta ${mapPreset.value?.name}` : 'Omställningskarta'}`,
  description: () =>
    `${
      mapPreset.value?.description
        ? mapPreset.value.description
        : 'Omställningskarta för fossilfria alternativ'
    }`,
  ogTitle: () =>
    `Drivmedla - ${mapPreset ? `Karta ${mapPreset.value?.name}` : 'Omställningskarta'}`,
  ogDescription: () =>
    `${
      mapPreset.value?.description
        ? mapPreset.value.description
        : 'Omställningskarta för fossilfria alternativ'
    }`,
  ogLocale: () => 'sv_SE',
  ogSiteName: () => 'Drivmedla',
  ogUrl: () => `${useRuntimeConfig().public.baseURL}${useRoute().fullPath}`,
  ogImage: () => `${useRuntimeConfig().public.baseURL}/_nuxt/assets/_logo-horizontal-GREEN.svg`,
})
</script>

<template>
  <main class="main">
    <MapSplitView>
      <template #content>
        <AppLink
          class="link-back"
          title="Utforska mer inom kartor & statistik"
          path="/kartor-och-statistik"
          icon="ArrowLeftIcon" />
        <AppLoadingWrapper :is-loading="!mapPreset" :height="20">
          <div v-if="mapPreset">
            <AppHeading :title="mapPreset.name" type="h1" />
            <!-- eslint-disable vue/no-v-html -->
            <p
              v-if="mapPreset.description"
              v-html="mapPreset.description.replace(/\n/g, '<br />')"
            ></p>
            <p v-if="mapPreset.content" v-html="mapPreset.content.replace(/\n/g, '<br />')"></p>
            <!--eslint-enable-->
            <!-- <p>{{ mapPreset.identifier }}</p> -->
            <DataViewInfrastructureOverall
              v-if="mapPreset.identifier === 'infrastructure-overall'"
            />
            <DataViewVehicleFleet v-if="mapPreset.identifier === 'vehicle-fleet'" />
            <DataViewMatchmaking v-if="mapPreset.identifier === 'infrastructure-opportunities'" />
            <DataViewInInfrastructureCharging
              v-if="mapPreset.identifier === 'infrastructure-charging-stations'"
            />
            <DataViewInInfrastructureLiquid
              v-if="mapPreset.identifier === 'infrastructure-liquid-fuel-stations'"
            />
            <DataViewInInfrastructureGas
              v-if="mapPreset.identifier === 'infrastructure-gas-fuel-stations'"
            />
            <DataViewElectricityGrid v-if="mapPreset.identifier === 'electricity-grid-owners'" />
            <DataViewAfir v-if="mapPreset.identifier === 'afir'" />
          </div> </AppLoadingWrapper
      ></template>
      <template #map>
        <AppMap v-if="loadMap" :layer-presets="layerPresets" />
        <div v-else class="loader-box"><AppLoading /></div>
      </template>
    </MapSplitView>
  </main>
</template>
<style lang="scss" scoped>
@import 'App.scss';
.loader-box {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}
.main {
  display: flex;
  height: calc(100% - $topbarHeight);
  margin: 0;
  flex-flow: column;
}
.data {
  padding: 24px;
  height: 100%;
  overflow-y: scroll;
  background-color: #fff;
  flex: 3;
}
.map {
  height: 100%;
  width: 100%;
  padding: 0;
  flex: 1;
}
</style>
```
